import { gql, type ApolloClient } from '@apollo/client';

import { SAVE_SURVEILLANCE_ROUTE } from '../mutations/surveillanceRoute';
import { writeOfflineOptimisticSurveillanceRouteUpdate } from '../cacheHelpers';
import { isOfflineQueuedError } from '../../offline/isOfflineQueuedError';
import type { SaveSurveillanceRouteInput } from '../../types/surveillanceRoute';

/**
 * Guarda una edición del recorrido, con red o sin ella (2026-09-23).
 *
 * El envío es el mismo de siempre: si no hay cobertura, `OfflineQueueLink` lo
 * encola y la mutación rechaza con el centinela de «encolada». Lo que faltaba
 * era lo de después: nadie escribía el cambio en la caché, así que la tablet
 * seguía viendo el recorrido de antes hasta sincronizar. Para la vía de apoyo
 * eso significaba que añadirla sin red no cambiaba nada en el cronómetro.
 *
 * Con red NO se escribe a mano: la respuesta del servidor ya normaliza la
 * entidad, y adelantarse solo podría pisarla con algo menos exacto.
 *
 * Lo usan el asistente `EditRoute` y el botón «Añadir al recorrido» del aviso
 * del detector: los dos tienen que comportarse igual sin cobertura.
 *
 * @returns `'saved'` si el servidor contestó, `'queued'` si quedó encolada.
 *          Cualquier otro error se propaga tal cual.
 */
export async function submitSurveillanceRouteEdit(
  client: ApolloClient<unknown>,
  input: SaveSurveillanceRouteInput
): Promise<'saved' | 'queued'> {
  try {
    await client.mutate({
      mutation: SAVE_SURVEILLANCE_ROUTE,
      variables: { input },
    });
    return 'saved';
  } catch (err) {
    if (!isOfflineQueuedError(err)) throw err;
    writeOfflineOptimisticSurveillanceRouteUpdate(
      client.cache,
      input,
      input.id ? twinOrderIdOf(client, input.id) : null
    );
    return 'queued';
  }
}

/** La Order gemela del recorrido, leída de la caché (sin red no hay otra). */
function twinOrderIdOf(
  client: ApolloClient<unknown>,
  routeId: string
): string | null {
  const orderIds = client.cache.readFragment<{ orderIDs?: string[] }>({
    id: client.cache.identify({ __typename: 'SurveillanceRoute', id: routeId }),
    fragment: TWIN_ORDER_FRAGMENT,
  })?.orderIDs;
  return orderIds?.[0] ?? null;
}

// Solo lo que hace falta: pedir el fragmento entero exigiría tenerlo completo
// en caché, y un recorrido a medias devolvería `null`.
const TWIN_ORDER_FRAGMENT = gql`
  fragment SurveillanceRouteTwinOrder on SurveillanceRoute {
    orderIDs
  }
`;
